import { createSlice } from "@reduxjs/toolkit";
import { jwtDecode } from "jwt-decode";
import auth0 from 'auth0-js';
import API, { ENDPOINTS } from "../../services/api";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../../services/firebase";
import { AppRoutes } from "./types/app-routes";
import { axiosInstance, setSelectedShop } from "./userSlices";
import { environment } from "environment";
export enum LocalStorageKeys {
  userToken = "userToken",
}

const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  isLoadingAutoLogin: false,
  error: null,
  isEmailSent: false,
  isPasswordSent: false,
  isLoaded: false,
  eulaAccepted: true
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setIsLoadingAutoLogin: (state, action) => {
      state.isLoadingAutoLogin = action.payload;
    },
    setIsEmailSent: (state, action) => {
      state.isEmailSent = action.payload;
    },
    setIsPasswordSent: (state, action) => {
      state.isPasswordSent = action.payload;
    },
    setMailSent: (state, action) => {
      state.isEmailSent = action.payload;
    },
    setAuthSuccess: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.isLoadingAutoLogin = false;
      state.isLoading = false;
    },
    setUpdateUserShops: (state, action) => {
      state.user = { ...state.user, shops: action.payload };
    },
    setLogout: (state) => {
      state.user = null;
      state.isLoading = false;
      state.isAuthenticated = false;
      state.isLoadingAutoLogin = false;
      state.eulaAccepted = false;
    },
    setAuthFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    setErrorNull: (state) => {
      state.error = null;
    },
    setEulaAccepted: (state) => {
      state.user = { ...state.user, eulaAccepted: true };
      state.eulaAccepted = true;
    },
    setIsLoaded: (state, action) => {
      state.isLoaded = action.payload;
    },
    setEulaNotAccepted: (state) => {
      state.eulaAccepted = false;
      
    }
  },
});

const webAuth = new auth0.WebAuth({
  domain: 'dev-nt0zjo2frfiyoocn.us.auth0.com',
  clientID: 'aEiTKrD8Q2gYw6vBcjGMfym5g960WQVQ',
  responseType: 'token id_token',
  scope: 'openid profile email',
  redirectUri: 'https://amorino-fe-dw2yd.ondigitalocean.app'
});


const getUserData = (idToken) => {

  if (!idToken) {
    console.error('No ID token found. User is not logged in.');
    return null;
  }

  try {
    const decoded = jwtDecode(idToken);
    return decoded; // This contains user claims like email, name, etc.
  } catch (error) {
    console.error('Failed to decode ID token:', error);
    return null;
  }
};

export const auth0Login =
  (email: string, password: string) => async (dispatch: Function) => {
    dispatch(setIsLoading(true));
    dispatch(setIsLoaded(true));
    dispatch(setErrorNull());
    try {
      webAuth.client.login(
        {
          realm: 'Username-Password-Authentication', // Replace with your connection name
          username: email,
          password: password,
          scope: 'openid profile email',
        },
        async (err, authResult) => {
          if (err) {
            dispatch(setAuthFailure(err.description || 'Login failed'));
            console.error(err);
            return;
          }
          localStorage.setItem('accessToken', authResult.accessToken);
          localStorage.setItem(LocalStorageKeys.userToken, authResult.idToken);
          axiosInstance.defaults.headers.Authorization = `Bearer ${authResult.idToken}`;
          const auth0User = getUserData(authResult.idToken);
          const res = await axiosInstance.get(`${environment.serverURL}/users/${auth0User.sub}`)
          const shops = res.status === 200 ? res.data.shops : []
          const userData = {
            uid: '1234',
            email: auth0User['email'],
            token: authResult.idToken,
            role: auth0User['role'][0],
            roleLevel: auth0User['roleLevel'],
            shops: shops,
            isSuperAdmin: auth0User['user_metadata']['is_super_admin'] || false,
            ...auth0User,
          };
          if(userData.shops.length)
            dispatch(setSelectedShop(userData.shops[0]))
          dispatch(setAuthSuccess(userData));
          dispatch(setEulaNotAccepted())
          dispatch(setIsLoading(false));
        }
      );

    } catch (error) {
      console.error(error);
      dispatch(setAuthFailure(error));
      dispatch(setIsLoading(false));
    } finally {
      dispatch(setIsLoaded(true));
    }
  };

  export const auth0Logout = (navigate) => async (dispatch: Function) => {
    try {
      // Clear local storage
      localStorage.removeItem(LocalStorageKeys.userToken);
      localStorage.removeItem('accessToken');
      
      // Clear axios default headers
      delete axiosInstance.defaults.headers.Authorization;
      
      // Dispatch logout action
      dispatch(setLogout());
      
      // Redirect to Auth0 logout
      // webAuth.logout({
      //   clientID: 'aEiTKrD8Q2gYw6vBcjGMfym5g960WQVQ',
      //   returnTo: 'https://google.com',
      // });
      
      navigate(AppRoutes.LOGIN);
    } catch (error) {
      console.error("Could not sign out from Auth0:", error);
    }
  };
  
  export const auth0SendPasswordReset = (email: string) => async (dispatch: Function) => {
    dispatch(setIsLoading(true));
    dispatch(setIsEmailSent(false));
    dispatch(setErrorNull());
  
    try {
      webAuth.changePassword({
        connection: 'Username-Password-Authentication',
        email: email
      }, (err, resp) => {
        if (err) {
          console.error('Error sending password reset email:', err);
          dispatch(setAuthFailure(err));
          dispatch(setIsLoading(false));
          return;
        }
        
        dispatch(setIsEmailSent(true));
        dispatch(setIsLoading(false));
      });
    } catch (error) {
      console.error('Error in password reset:', error);
      dispatch(setAuthFailure(error));
      dispatch(setIsLoading(false));
    }
  };
  
  export const auth0ChangePassword = (
    email: string, 
    oldPassword: string, 
    newPassword: string
  ) => async (dispatch: Function) => {
    dispatch(setIsLoading(true));
    dispatch(setErrorNull());
  
    try {
      webAuth.client.login({
        realm: 'Username-Password-Authentication',
        username: email,
        password: oldPassword
      }, (err, response) => {
        if (err) {
          console.error('Error validating old password:', err);
          dispatch(setAuthFailure({ code: 'OLD_PASSWORD_INVALID' }));
          dispatch(setIsLoading(false));
          return;
        }
  
        // If old password is valid, proceed with password change
        webAuth.changePassword({
          connection: 'Username-Password-Authentication',
          email: email,
          password: newPassword
        }, (changeErr, changeResponse) => {
          if (changeErr) {
            console.error('Error changing password:', changeErr);
            dispatch(setAuthFailure(changeErr));
            dispatch(setIsLoading(false));
            return;
          }
  
          dispatch(setIsPasswordSent(true));
          dispatch(setIsLoading(false));
        });
      });
    } catch (error) {
      console.error('Error in change password flow:', error);
      dispatch(setAuthFailure(error));
      dispatch(setIsLoading(false));
    }
  };


const isTokenValid = (token) => {
  if (!token) return false;

  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000; // Convert milliseconds to seconds
    return decoded.exp > currentTime; // Check if the token has expired
  } catch (error) {
    console.error('Invalid token:', error);
    return false;
  }
};
export const refreshUserInfos = async (
  dispatch: Function,
  navigate,
  location
) => {
  try {
    const token = localStorage.getItem(LocalStorageKeys.userToken);
    const auth0User = getUserData(token);
    axiosInstance.defaults.headers.Authorization = `Bearer ${token}`;
    const res = await axiosInstance.get(`${environment.serverURL}/users/${auth0User.sub}`)
    const shops = res.status === 200 ? res.data.shops : []
    dispatch(setUpdateUserShops(shops))
          
  } catch (e) {
    console.error(e);
  }
};
export const checkAutoLogin = async (
  dispatch: Function,
  navigate,
  location
) => {
  dispatch(setIsLoaded(false));
  dispatch(setIsLoading(true));
  try {
    const token = localStorage.getItem(LocalStorageKeys.userToken);
    if (!isTokenValid(token)) {
      dispatch(setLogout());
      return;
    }

    if (location.pathname !== "/set-my-password") {
      const auth0User = getUserData(token);
      axiosInstance.defaults.headers.Authorization = `Bearer ${token}`;
      const res = await axiosInstance.get(`${environment.serverURL}/users/${auth0User.sub}`)
          const shops = res.status === 200 ? res.data.shops : []
          const userData = {
            uid: '1234',
            email: auth0User['email'],
            token,
            role: auth0User['role'][0],
            roleLevel: auth0User['roleLevel'],
            shops: shops,
            test:'',
            isSuperAdmin: auth0User['user_metadata']['is_super_admin'] || false,
            ...auth0User,
          };
          if(userData.shops.length)
            dispatch(setSelectedShop(userData.shops[0].id))
          dispatch(setAuthSuccess(userData));
      navigate(location.pathname + location.search);
    } else {
      dispatch(setIsLoadingAutoLogin(false));
    }
  } catch (e) {
    console.error(e);
  } finally {
    dispatch(setIsLoaded(true));
    dispatch(setIsLoading(false));
  }
};

export const {
  setIsLoading,
  setIsLoadingAutoLogin,
  setAuthSuccess,
  setAuthFailure,
  setErrorNull,
  setLogout,
  setIsEmailSent,
  setIsPasswordSent,
  setIsLoaded,
  setEulaAccepted,
  setEulaNotAccepted,
  setUpdateUserShops
} = authSlice.actions;

export default authSlice.reducer;
